import React, { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Select, Space, Switch, Tag } from 'antd'
import { parsePolicy, parseExemptions } from './model'
import { menuAdminRequest, errorMessage } from './api'
import { policyCatalog, validateSelectionPolicy } from './policySelection'
import { policySummary } from './policySummary'
import PolicySystems from './PolicySystems'
import { GLOBAL_POLICY_MODES } from './policyDisplay'
import HelpHint from './HelpHint'

// 2026-10-27 coder(lq): 页面直接勾选真实系统/菜单，所有开关仍整体写入已有数据库策略；不导入数据、不操作配置中心。
export default function PolicyPanel({ settings, onReview, onError }) {
    const [policy, setPolicy] = useState(null)
    const [scopes, setScopes] = useState([])
    const [loading, setLoading] = useState(true)
    const [navigationError, setNavigationError] = useState('')
    const [refresh, setRefresh] = useState(0)
    const [exemptionsText, setExemptionsText] = useState('')
    useEffect(() => {
        try {
            const value = validateSelectionPolicy(parsePolicy(settings.policyJson)); setPolicy(value)
            setExemptionsText(value.exemptions.map(item => `${item.method} ${item.path}`).join('\n'))
        } catch (error) { setPolicy(null); onError(error.message) }
    }, [settings.policyJson, onError])
    useEffect(() => {
        let active = true
        setLoading(true); setNavigationError('')
        menuAdminRequest('navigation').then(result => {
            if (!result || result.kind !== 'ALL' || !Array.isArray(result.children)) throw new Error('菜单导航响应不完整，请刷新或升级导航接口')
            const catalog = policyCatalog(result)
            if (active) setScopes(catalog)
        }).catch(error => { if (active) { setScopes([]); setNavigationError(errorMessage(error)) } })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [refresh, settings.effectivePolicyJson])
    if (!policy) return null
    const before = parsePolicy(settings.policyJson)
    const dirty = JSON.stringify(policy) !== JSON.stringify(before)
        || exemptionsText !== before.exemptions.map(item => `${item.method} ${item.path}`).join('\n')
    const review = () => {
        try {
            if ((loading || navigationError) && policy.enabled) throw new Error('请先完整读取系统菜单，再开启权限校验')
            const next = validateSelectionPolicy({ ...policy, exemptions: parseExemptions(exemptionsText) })
            onReview(next, policySummary(scopes, before, next))
        } catch (error) { onError(error.message) }
    }
    return <section aria-label="系统权限开关配置">
        <div className="menu-api-policy-global">
            <Space className="menu-api-policy-global-controls"><strong>权限校验总开关</strong>
                <Switch aria-label="权限校验总开关" checked={policy.enabled} checkedChildren="开启" unCheckedChildren="关闭"
                    onChange={enabled => setPolicy({ ...policy, enabled, gatewayMode: enabled && policy.gatewayMode === 'OFF' ? 'AUDIT' : policy.gatewayMode })} />
                <HelpHint label="运行策略说明">全部权限开关在本页管理，不再使用 Apollo 配置。“全部仅观察”会暂时阻止所有系统执行新增权限拦截，但不改变各系统设置；“按各系统设置执行”才按右侧的本系统运行方式处理，不会自动把所有系统设为拦截。
                    首次开启默认全部仅观察；重新开启保留已选的全局安全模式。总开关关闭时不启用本功能，原有登录校验不变。
                    所有勾选和开关均为待保存修改，核对并确认后才保存到数据库；保存成功不代表网关接入已验证。</HelpHint>
                <span>全局安全模式</span><Select aria-label="全局安全模式" className="menu-api-policy-mode menu-api-policy-global-mode"
                    value={policy.gatewayMode === 'OFF' ? undefined : policy.gatewayMode} placeholder="开启后默认全部仅观察"
                    onChange={gatewayMode => setPolicy({ ...policy, gatewayMode })}
                    options={['AUDIT', 'ENFORCE'].map(value => ({ value, label: GLOBAL_POLICY_MODES[value] }))} />
            </Space>
            <Space className="menu-api-policy-actions"><Tag>{dirty ? '有未保存修改' : '已与保存配置一致'}</Tag>
                <Button danger onClick={() => {
                    setPolicy({ ...before, enabled: false, gatewayMode: 'OFF' })
                    setExemptionsText(before.exemptions.map(item => `${item.method} ${item.path}`).join('\n'))
                }}>全部停用（待确认）</Button>
                <Button type="primary" onClick={review} disabled={loading && policy.enabled}>核对并保存策略</Button></Space>
        </div>
        <Space className="menu-api-policy-saved"><span>已保存总开关：{settings.enabled ? '开启' : '关闭'}</span>
            <span>已保存全局安全模式：{GLOBAL_POLICY_MODES[settings.gatewayMode || 'OFF']}</span>
            <HelpHint label="已保存状态说明">此处为服务端返回的已保存状态，与上方草稿可能不同；实际拦截还依赖网关已部署并成功读取策略。</HelpHint></Space>
        {navigationError ? <Alert type="error" showIcon message={navigationError}
            action={<Button size="small" onClick={() => setRefresh(value => value + 1)}>重试读取菜单</Button>} />
            : loading ? <div className="menu-api-loading" role="status">正在读取系统和菜单…</div>
                : <PolicySystems scopes={scopes} policy={policy} onChange={setPolicy} onError={onError} />}
        <details className="menu-api-policy-advanced"><summary>高级设置与技术明细</summary>
            <Form layout="vertical"><Form.Item label="接口豁免（每行一个方法和精确路径）">
                <Input.TextArea aria-label="接口豁免" rows={3} value={exemptionsText}
                    onChange={event => setExemptionsText(event.target.value)} placeholder="GET /service/path" />
            </Form.Item></Form>
            <Space><Tag>策略来源：{settings.source}</Tag><Tag>数据库版本：{settings.version}</Tag></Space>
            <HelpHint label="已有策略兼容说明">旧服务绑定和未读取到的菜单原样保留；本次只修改你操作系统的当前菜单范围，不删除失效菜单或CMS未读取关系。
                对旧服务规则的取消通过精确关闭覆盖，不会把售后与SCRM合并；未来新增菜单仍需核对旧规则和本页精确选择。</HelpHint>
            <pre>{settings.effectivePolicyJson}</pre>
        </details>
    </section>
}
